import type { SupabaseClient } from '../../lib/supabase';
import type { CommentCountsByTask, CommentWithAuthor, CreateCommentInput } from './types';
import { countCommentsByTask } from './types';

/**
 * Database access for comments and mentions.
 *
 * Every read is already filtered by Row Level Security and nothing here is
 * trusted for authorization: who may read, write, edit or delete a comment is
 * decided by the database (comments-access migration), proved by the Stage 1
 * staging assertions.
 */

function fail(message: string): never {
  throw new Error(message);
}

interface CommentRow {
  id: string;
  task_id: string | null;
  project_id: string;
  author_id: string;
  body: string;
  edited_at: string | null;
  created_at: string;
  parent_comment_id?: string | null;
  profiles: { display_name: string; email: string; avatar_url: string | null } | null;
  comment_mentions: { mentioned_user_id: string }[];
}

const COMMENT_SELECT =
  'id, task_id, project_id, author_id, body, edited_at, created_at, parent_comment_id, profiles:author_id (display_name, email, avatar_url), comment_mentions (mentioned_user_id)';

/**
 * One thread, oldest first (a conversation reads downward), each comment
 * joined to its author and its mentions in the same request. A null taskId
 * reads the project-level discussion: the comments that belong to the
 * project itself rather than to any task.
 */
export async function fetchTaskComments(
  client: SupabaseClient,
  taskId: string | null,
  projectId: string,
  dealId: string | null = null,
  socialPostId: string | null = null,
  /** Only the newest N (Messages rooms). Left out, the whole thread, as before. */
  limit?: number,
): Promise<CommentWithAuthor[]> {
  let query = client.from('task_comments').select(COMMENT_SELECT);
  query =
    socialPostId !== null
      ? query.eq('social_post_id', socialPostId)
      : dealId !== null
        ? query.eq('deal_id', dealId)
        : taskId === null
          ? query.eq('project_id', projectId).is('task_id', null)
          : query.eq('task_id', taskId);
  // A limit has to cut from the newest end, so ask newest first and turn the
  // rows back to oldest first below. No limit: oldest first, as always.
  const ordered = query.order('created_at', { ascending: limit === undefined });
  const { data, error } = await (limit === undefined ? ordered : ordered.limit(limit));

  if (error) {
    fail(error.message);
  }

  const rows = (data ?? []) as unknown as CommentRow[];
  return withQuotes(
    client,
    (limit === undefined ? rows : [...rows].reverse()).map(toComment),
    taskId === null && dealId === null && socialPostId === null,
  );
}

/**
 * The quote link lives in its own table (console_comment_quotes), not on
 * task_comments, so it is read in one extra request for Console rooms only.
 */
async function withQuotes(
  client: SupabaseClient,
  comments: CommentWithAuthor[],
  isRoom: boolean,
): Promise<CommentWithAuthor[]> {
  if (!isRoom || comments.length === 0) {
    return comments;
  }
  const { data, error } = await client
    .from('console_comment_quotes')
    .select('comment_id, quoted_comment_id')
    .in(
      'comment_id',
      comments.map((comment) => comment.id),
    );
  if (error) {
    fail(error.message);
  }
  const quoted = new Map((data ?? []).map((row) => [row.comment_id, row.quoted_comment_id]));
  return comments.map((comment) => ({
    ...comment,
    quoted_comment_id: quoted.get(comment.id) ?? null,
  }));
}

/**
 * Specific comments by id (the Pinned strip, which can name messages older
 * than the window a room has loaded). Same shape as a thread read.
 */
export async function fetchCommentsByIds(
  client: SupabaseClient,
  ids: string[],
): Promise<CommentWithAuthor[]> {
  if (ids.length === 0) {
    return [];
  }
  const { data, error } = await client.from('task_comments').select(COMMENT_SELECT).in('id', ids);
  if (error) {
    fail(error.message);
  }
  return withQuotes(client, ((data ?? []) as unknown as CommentRow[]).map(toComment), true);
}

function toComment(row: CommentRow): CommentWithAuthor {
  return {
    id: row.id,
    task_id: row.task_id,
    project_id: row.project_id,
    author_id: row.author_id,
    body: row.body,
    edited_at: row.edited_at,
    created_at: row.created_at,
    parent_comment_id: row.parent_comment_id ?? null,
    quoted_comment_id: null,
    author_name: row.profiles
      ? row.profiles.display_name.trim() !== ''
        ? row.profiles.display_name
        : row.profiles.email
      : '',
    author_avatar_url: row.profiles?.avatar_url ?? null,
    mentioned_user_ids: row.comment_mentions.map((mention) => mention.mentioned_user_id),
  };
}

/** Comment counts for every task in a project, for the List View indicator. */
export async function fetchCommentCounts(
  client: SupabaseClient,
  projectId: string,
): Promise<CommentCountsByTask> {
  const { data, error } = await client
    .from('task_comments')
    .select('task_id')
    .eq('project_id', projectId)
    .not('task_id', 'is', null);

  if (error) {
    fail(error.message);
  }
  return countCommentsByTask(data ?? []);
}

/**
 * A comment, then its mentions. Two requests rather than one transaction: if
 * a mention insert is refused (an inactive account, say) the comment itself
 * stands, which is the right outcome — words were said; one @name failed.
 * A mention failure does not throw for that reason, but it is no longer
 * silent either: the refusal comes back as mentionError (the database's own
 * message, for commentErrorMessage) so a screen can say nobody was notified.
 * All of one comment's mentions go in one insert, so one refusal means none
 * of them landed. Callers that ignore the result behave exactly as before.
 */
export interface CreateCommentResult {
  mentionError: string | null;
}

export async function createComment(
  client: SupabaseClient,
  input: CreateCommentInput & { authorId: string },
): Promise<CreateCommentResult> {
  if (input.quotedCommentId) {
    // The quote link lives in its own table; the browser cannot write it, so
    // the comment and its quote go in together through one checked function.
    const { error: quoteError } = await client.rpc('send_console_quote_owner', {
      p_body: input.body,
      p_source: input.quotedCommentId,
    });
    if (quoteError) {
      fail(quoteError.message);
    }
    return { mentionError: null };
  }
  const { data, error } = await client
    .from('task_comments')
    .insert({
      task_id: input.taskId,
      // A deal comment carries no project: exactly one parent, enforced by a
      // check constraint, so the unused side must be null rather than blank.
      // Exactly one parent, enforced by a check constraint: the two the
      // comment does not belong to are explicitly null.
      project_id:
        (input.dealId ?? null) === null && (input.socialPostId ?? null) === null
          ? input.projectId
          : null,
      deal_id: input.dealId ?? null,
      social_post_id: input.socialPostId ?? null,
      author_id: input.authorId,
      body: input.body.trim(),
    })
    .select('id')
    .single();

  if (error) {
    fail(error.message);
  }

  const uniqueIds = [...new Set(input.mentionedUserIds)];
  if (uniqueIds.length > 0) {
    const { error: mentionError } = await client.from('comment_mentions').insert(
      uniqueIds.map((userId) => ({
        comment_id: data.id,
        // Mirrors the comment's own parent: one or the other, never both.
        project_id:
          (input.dealId ?? null) === null && (input.socialPostId ?? null) === null
            ? input.projectId
            : null,
        deal_id: input.dealId ?? null,
        social_post_id: input.socialPostId ?? null,
        mentioned_user_id: userId,
      })),
    );
    if (mentionError) {
      return { mentionError: mentionError.message };
    }
  }
  return { mentionError: null };
}

/**
 * Bring a comment's mention rows in line with the mentions in its text.
 *
 * ── The bug this fixes ─────────────────────────────────────────────────────
 *
 * Editing a comment only ever sent the body. So a mention ADDED while editing
 * rendered a perfect pill, saved cleanly, and wrote no `comment_mentions` row
 * at all — which meant no notification, and nothing anywhere said so.
 *
 * Measured on the live data, one project, three comments:
 *
 *   21 Aug  "@Michael @Paul see attached"   both recorded; Paul not notified
 *   24 Aug  "@Paul @Michael Revision Brief" both recorded, both notified
 *   24 Aug  "UPDATED. @Michael @Paul"       NO ROWS AT ALL, nobody notified
 *
 * The third is this bug. Two pills on screen, nothing in the table. Kyu asked
 * both of them the next day and neither had heard anything.
 *
 * ── Why reconcile rather than replace ──────────────────────────────────────
 *
 * `comment_mentions_notify` fires AFTER INSERT, so deleting every row and
 * re-inserting would notify everybody again on every edit — a typo fix would
 * re-ping the whole thread. Only genuinely new mentions are inserted, and only
 * genuinely removed ones are deleted.
 *
 * A removed mention gets no "never mind" notification. There is nothing to
 * send, and the one already delivered was true when it went.
 */
export async function setCommentMentions(
  client: SupabaseClient,
  input: {
    commentId: string;
    projectId: string;
    dealId?: string | null;
    socialPostId?: string | null;
    mentionedUserIds: string[];
  },
): Promise<void> {
  const wanted = new Set(input.mentionedUserIds);

  const { data, error } = await client
    .from('comment_mentions')
    .select('mentioned_user_id')
    .eq('comment_id', input.commentId);
  if (error) {
    fail(error.message);
  }
  const existing = new Set((data ?? []).map((row) => row.mentioned_user_id));

  const added = [...wanted].filter((id) => !existing.has(id));
  const removed = [...existing].filter((id) => !wanted.has(id));

  if (removed.length > 0) {
    await client
      .from('comment_mentions')
      .delete()
      .eq('comment_id', input.commentId)
      .in('mentioned_user_id', removed);
  }

  if (added.length > 0) {
    await client.from('comment_mentions').insert(
      added.map((userId) => ({
        comment_id: input.commentId,
        // Mirrors the comment's own parent: one or the other, never both.
        project_id:
          (input.dealId ?? null) === null && (input.socialPostId ?? null) === null
            ? input.projectId
            : null,
        deal_id: input.dealId ?? null,
        social_post_id: input.socialPostId ?? null,
        mentioned_user_id: userId,
      })),
    );
  }
}

/**
 * Body only: it is the single column the database grants. The edited
 * indicator is stamped by trigger, so nothing else is sent.
 */
export async function updateCommentBody(
  client: SupabaseClient,
  commentId: string,
  body: string,
): Promise<void> {
  const { data, error } = await client
    .from('task_comments')
    .update({ body: body.trim() })
    .eq('id', commentId)
    .select('id');

  if (error) {
    fail(error.message);
  }
  // Editing someone else's comment is filtered by policy rather than refused:
  // zero rows came back. Surface it instead of pretending the edit landed.
  if ((data ?? []).length === 0) {
    fail('comment_edit_not_permitted');
  }
}

/** The author removes their own; managers may remove anything (handoff 21). */
export async function deleteComment(client: SupabaseClient, commentId: string): Promise<void> {
  const { error } = await client.from('task_comments').delete().eq('id', commentId);
  if (error) {
    fail(error.message);
  }
}

/**
 * Database refusals worth explaining. The trigger wordings are stable; the
 * last two are raised client-side by this file.
 */
export const COMMENT_ERROR_MESSAGES: Record<string, string> = {
  comment_project_must_match_task: 'This comment belongs to a different project.',
  comment_identity_is_immutable: 'Only the text of a comment can be edited.',
  mention_project_must_match_comment: 'That mention belongs to a different project.',
  mention_must_be_active_tap_user: 'That person cannot be mentioned.',
  comment_edit_not_permitted: 'Only the author can edit a comment.',
};

/** Turns a database error message into something a person can act on. */
export function commentErrorMessage(message: string): string {
  return COMMENT_ERROR_MESSAGES[message] ?? 'That comment could not be saved. Try again.';
}

/** Older room rows under RLS for a quote jump beyond the capped main window. */
export async function fetchOlderRoomComments(
  client: SupabaseClient,
  roomId: string,
  cursor: { id: string; created_at: string },
): Promise<CommentWithAuthor[]> {
  // Values come from server rows, but validate before interpolating PostgREST's filter syntax.
  if (!/^[0-9a-f-]{36}$/i.test(cursor.id) || !/^[0-9T:.+Z-]+$/.test(cursor.created_at))
    throw new Error('Invalid message cursor');
  const { data, error } = await client
    .from('task_comments')
    .select(COMMENT_SELECT)
    .eq('project_id', roomId)
    .is('task_id', null)
    .is('deleted_at', null)
    .or(
      `created_at.lt.${cursor.created_at},and(created_at.eq.${cursor.created_at},id.lt.${cursor.id})`,
    )
    .order('created_at', { ascending: false })
    .order('id', { ascending: false })
    .limit(25);
  if (error) throw new Error(error.message);
  return ((data ?? []) as unknown as CommentRow[]).map(toComment);
}
